import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
} from '@react-pdf/renderer';
import moment from 'moment';

export interface IPdfComissaoProdutorData {
  code: string;
  operadora_name: string;
  operadora_code: string;
  modalidade_name: string;
  modalidade_code: string;
  administradora_name: string | null;
  administradora_code: string | null;
  date_vigencia: string;
  is_active: boolean;
  vitalicio: number | null;
  produtor_nome?: string;
  produtor_code?: string;
  parcelas: { parc: number; value: number }[];
}

const styles = StyleSheet.create({
  page: {
    padding: 32,
    fontSize: 10,
    fontFamily: 'Helvetica',
    color: '#1f2937',
  },
  header: {
    marginBottom: 20,
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#3980F5',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 10,
    color: '#6b7280',
  },
  infoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 20,
    gap: 8,
  },
  infoCard: {
    width: '48%',
    padding: 10,
    borderRadius: 6,
    backgroundColor: '#f8fafc',
    border: '1 solid #e5e7eb',
  },
  infoLabel: {
    fontSize: 8,
    color: '#6b7280',
    marginBottom: 2,
    textTransform: 'uppercase',
  },
  infoValue: {
    fontSize: 11,
    fontWeight: 'bold',
  },
  table: {
    border: '1 solid #e5e7eb',
    borderRadius: 6,
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#3980F5',
    color: '#fff',
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableRowAlt: {
    flexDirection: 'row',
    backgroundColor: '#f9fafb',
  },
  tableCellHeader: {
    flex: 1,
    padding: 8,
    fontWeight: 'bold',
    fontSize: 9,
  },
  tableCell: {
    flex: 1,
    padding: 8,
    borderBottom: '1 solid #e5e7eb',
    fontSize: 9,
  },
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 32,
    right: 32,
    fontSize: 8,
    textAlign: 'center',
    color: '#9ca3af',
  },
});

interface IPdfComissaoProdutorProps {
  data: IPdfComissaoProdutorData;
}

export const PdfComissaoProdutor = ({ data }: IPdfComissaoProdutorProps) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* HEADER */}
        <View style={styles.header}>
          <Text style={styles.title}>Tabela de Comissão - Produtor</Text>
          <Text style={styles.subtitle}>
            Código: {data.code} • Operadora: {data.operadora_name} • Vigência:{' '}
            {moment(data.date_vigencia).format('DD/MM/YYYY')}
          </Text>
        </View>

        {/* INFO CARDS */}
        <View style={styles.infoGrid}>
          <View style={styles.infoCard}>
            <Text style={styles.infoLabel}>Operadora</Text>
            <Text style={styles.infoValue}>
              {data.operadora_code} - {data.operadora_name}
            </Text>
          </View>

          <View style={styles.infoCard}>
            <Text style={styles.infoLabel}>Modalidade</Text>
            <Text style={styles.infoValue}>
              {data.modalidade_code} - {data.modalidade_name}
            </Text>
          </View>

          {data.administradora_name && (
            <View style={styles.infoCard}>
              <Text style={styles.infoLabel}>Administradora</Text>
              <Text style={styles.infoValue}>
                {data.administradora_code} - {data.administradora_name}
              </Text>
            </View>
          )}

          {data.produtor_nome && (
            <View style={styles.infoCard}>
              <Text style={styles.infoLabel}>Produtor</Text>
              <Text style={styles.infoValue}>
                {data.produtor_code} - {data.produtor_nome}
              </Text>
            </View>
          )}

          <View style={styles.infoCard}>
            <Text style={styles.infoLabel}>Vitalício</Text>
            <Text style={styles.infoValue}>
              {data.vitalicio ? `A partir da ${data.vitalicio}ª parcela` : 'Não definido'}
            </Text>
          </View>

          <View style={styles.infoCard}>
            <Text style={styles.infoLabel}>Status</Text>
            <Text style={styles.infoValue}>
              {data.is_active ? 'Ativo' : 'Inativo'}
            </Text>
          </View>
        </View>

        {/* TABLE */}
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={styles.tableCellHeader}>Parcela</Text>
            <Text style={styles.tableCellHeader}>Comissão (%)</Text>
          </View>

          {data.parcelas.map((item, index) => (
            <View
              key={item.parc}
              style={index % 2 === 0 ? styles.tableRow : styles.tableRowAlt}
            >
              <Text style={styles.tableCell}>{item.parc}ª parcela</Text>
              <Text style={styles.tableCell}>{item.value}%</Text>
            </View>
          ))}
        </View>

        {/* FOOTER */}
        <Text style={styles.footer}>
          Documento gerado em {moment().format('DD/MM/YYYY [às] HH:mm')}
        </Text>
      </Page>
    </Document>
  );
};

/**
 * Função para gerar e fazer download do PDF
 */
export const handleDownloadPdfComissaoProdutor = async (
  data: IPdfComissaoProdutorData
) => {
  const blob = await pdf(<PdfComissaoProdutor data={data} />).toBlob();
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `comissao-produtor-${data.code}-${data.operadora_name}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};
